<template>
  <AppNavbar />
  <div class="vx-admin">

    <div v-if="cargando" class="vx-panel vx-vacio">
      <div class="spinner-border spinner-border-sm text-secondary" role="status"></div>
      <span class="vx-vacio-texto">Cargando…</span>
    </div>

    <div v-else-if="error" class="vx-panel vx-vacio">
      <i class="bi bi-exclamation-triangle" style="font-size: 22px; color: var(--vx-texto-4)"></i>
      <span class="vx-vacio-titulo">No se pudo abrir la suscripción</span>
      <span class="vx-vacio-texto">{{ error }}</span>
      <router-link class="vx-btn vx-btn-sm mt-1" :to="{ name: 'Suscripciones' }">Volver al listado</router-link>
    </div>

    <template v-else-if="suscripcion">
      <!-- Identidad -->
      <div class="d-flex align-items-start justify-content-between mb-3">
        <div>
          <div class="vx-migas mb-1">
            <span>Cobranza</span><span class="sep">/</span>
            <router-link :to="{ name: 'Suscripciones' }">Suscripciones</router-link>
            <span class="sep">/</span>
            <span class="vx-num">#{{ suscripcion.cr_suscripcion_id }}</span>
          </div>
          <div class="d-flex align-items-center gap-2 mb-1">
            <h1 class="vx-titulo">{{ titulo }}</h1>
            <span class="vx-estado" :class="claseEstado">{{ textoEstado }}</span>
          </div>
          <div class="vx-ayuda">{{ subtitulo }}</div>
        </div>
        <div v-if="puedeAdministrar" class="d-flex gap-2">
          <button
            class="vx-btn"
            :disabled="guardando || suscripcion.estado === 'FINALIZADA'"
            @click="alternarSuspension"
          >
            {{ suscripcion.estado === 'SUSPENDIDA' ? 'Reactivar' : 'Suspender' }}
          </button>
          <button
            v-if="suscripcion.estado !== 'FINALIZADA'"
            class="vx-btn"
            data-test="btn-abrir-baja"
            @click="abrirBaja"
          >
            Dar de baja
          </button>
        </div>
      </div>

      <div class="row g-3">
        <!-- Columna izquierda -->
        <div class="col-lg-7">

          <!-- Baja de la suscripción (RN-23, D6). Se previsualiza antes de
               confirmar porque puede anular nueve cuotas de una sola vez, y
               porque "cuáles NO se tocan" es información que el operador
               necesita ANTES de decidir. -->
          <div v-if="baja" class="vx-panel mb-3" data-test="panel-baja">
            <div class="vx-panel-cabecera">
              <h2 class="vx-panel-titulo">Dar de baja</h2>
              <button class="vx-btn vx-btn-sm vx-btn-texto" @click="baja = null">Cancelar</button>
            </div>
            <div class="p-3">
              <div class="row g-3">
                <div class="col-md-5">
                  <label class="vx-etiqueta" for="baja-fecha">Vigente hasta</label>
                  <input
                    id="baja-fecha" v-model="baja.vigencia_hasta" class="vx-campo"
                    type="date" @change="recalcularBaja"
                  >
                </div>
                <div class="col-md-7">
                  <label class="vx-etiqueta" for="baja-motivo">Motivo</label>
                  <input
                    id="baja-motivo" v-model.trim="baja.motivo" class="vx-campo"
                    placeholder="Por ejemplo: se cambió de institución"
                    :class="{ 'vx-campo-error': errorBaja }"
                  >
                </div>
              </div>

              <label class="vx-ayuda d-flex align-items-center gap-2 mt-3 mb-0">
                <input v-model="baja.anular_futuras" type="checkbox" data-test="baja-anular">
                Anular las cuotas futuras impagas
              </label>

              <div v-if="cargandoBaja" class="vx-ayuda mt-3">Calculando…</div>

              <template v-else-if="baja.previsualizacion">
                <div v-if="baja.anular_futuras && baja.anulables.length" class="vx-aviso mt-3"
                     data-test="baja-anulables">
                  <div class="vx-aviso-titulo">
                    <i class="bi bi-x-circle"></i>
                    Se anulan {{ baja.anulables.length }}
                    {{ baja.anulables.length === 1 ? 'cuota' : 'cuotas' }} por
                    {{ formatearMonto(baja.monto_anulable) }}
                  </div>
                  <div class="d-flex flex-wrap gap-1 mt-2">
                    <span v-for="d in baja.anulables" :key="d.cr_deuda_id" class="vx-chip vx-num">
                      {{ d.periodo }}
                    </span>
                  </div>
                </div>

                <div v-else-if="baja.anular_futuras" class="vx-nota mt-3">
                  <i class="bi bi-info-circle"></i>
                  <span>No hay cuotas futuras impagas para anular.</span>
                </div>

                <!-- Lo que NO se toca, con el motivo. Anular de menos sin
                     avisar es peor que no anular. -->
                <div v-if="baja.intocables.length" class="vx-nota mt-3" data-test="baja-intocables">
                  <i class="bi bi-shield-check"></i>
                  <span>
                    {{ baja.intocables.length }}
                    {{ baja.intocables.length === 1 ? 'cuota queda' : 'cuotas quedan' }} en pie:
                    {{ resumenIntocables }}.
                  </span>
                </div>
              </template>

              <div v-if="errorBaja" class="vx-error mt-2" data-test="error-baja">{{ errorBaja }}</div>

              <div class="d-flex justify-content-end gap-2 mt-3">
                <button class="vx-btn" @click="baja = null">Cancelar</button>
                <button
                  class="vx-btn vx-btn-primario"
                  data-test="btn-confirmar-baja"
                  :disabled="guardandoBaja"
                  @click="confirmarBaja"
                >
                  <span v-if="guardandoBaja" class="spinner-border spinner-border-sm"></span>
                  Dar de baja
                </button>
              </div>
            </div>
          </div>

          <!-- Cuotas generadas (E2). Antes de la generación esto era un vacío
               honesto; ahora muestra lo que hay, y el vacío sigue explicando
               por qué está vacío en vez de dejar una tabla muda. -->
          <div class="vx-panel mb-3">
            <div class="vx-panel-cabecera">
              <div>
                <h2 class="vx-panel-titulo">{{ vocabulario.plural }}</h2>
                <div v-if="deudas.length" class="vx-ayuda mt-1">
                  {{ deudas.length }} generadas · saldo {{ formatearMonto(saldoTotal) }}
                </div>
              </div>

            </div>

            <div v-if="cargandoDeudas" class="vx-vacio">
              <div class="spinner-border spinner-border-sm text-secondary" role="status"></div>
              <span class="vx-vacio-texto">Cargando…</span>
            </div>

            <div v-else-if="!deudas.length" class="vx-vacio" data-test="cuotas-vacias">
              <i class="bi bi-calendar3" style="font-size: 22px; color: var(--vx-texto-4)"></i>
              <span class="vx-vacio-titulo">Todavía no se generó ningún período</span>
              <span class="vx-vacio-texto">
                Las cuotas no se crean desde acá: se generan para
                <strong>todo el esquema</strong> a la vez, desde
                <router-link v-if="puedeGenerar" :to="{ name: 'Generacion', query: { esquema: suscripcion.cr_esquema_id } }">
                  Cobranza → Generar período</router-link><span v-else>Cobranza → Generar período</span>.
                Ahí se previsualiza antes de confirmar.
              </span>
            </div>

            <table v-else class="vx-tabla" data-test="tabla-cuotas">
              <thead>
                <tr>
                  <th>Período</th>
                  <th>Concepto</th>
                  <th>Vence</th>
                  <th class="vx-col-num">Total</th>
                  <th class="vx-col-num">Saldo</th>
                  <th>Estado</th>
                  <th style="width: 44px"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="d in deudas" :key="d.cr_deuda_id">
                  <td class="vx-num">{{ d.periodo }}</td>
                  <td>
                    <div class="vx-celda-principal">{{ d.descripcion }}</div>
                    <!-- El descuento va CONGELADO en la deuda (RN-16): se
                         muestra el que se aplicó, no el que rige hoy. -->
                    <div v-if="Number(d.monto_descuento)" class="vx-celda-sub">
                      Descuento aplicado − {{ formatearMonto(d.monto_descuento) }}
                    </div>
                  </td>
                  <td class="vx-num">{{ fechaCorta(d.fecha_vencimiento) }}</td>
                  <td class="vx-col-num vx-num">{{ formatearMonto(d.monto_total) }}</td>
                  <td class="vx-col-num vx-num" style="font-weight: 600">
                    {{ formatearMonto(d.saldo) }}
                  </td>
                  <td>
                    <span class="vx-estado" :class="estadoDeuda(d).clase">{{ estadoDeuda(d).texto }}</span>
                  </td>
                  <td class="text-end">
                    <!-- Sólo se ofrece donde la regla lo permite: una cuota con
                         pagos aplicados NUNCA se reasigna (RN-6), y el backend
                         la rechaza igual. Ofrecer el botón y que falle sería
                         hacerle perder el viaje al operador. -->
                    <button
                      v-if="puedeReasignar && esReasignable(d)"
                      class="vx-btn vx-btn-sm"
                      :data-test="`reasignar-${d.cr_deuda_id}`"
                      title="Reasignar a otro responsable"
                      @click="abrirReasignacion(d)"
                    >
                      <i class="bi bi-arrow-left-right"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Reasignación de una cuota impaga (RN-6). Es una corrección sobre
               plata: pide motivo y queda registrada con actor (artículo 6). -->
          <div v-if="reasignando" class="vx-panel mb-3" data-test="panel-reasignacion">
            <div class="vx-panel-cabecera">
              <h2 class="vx-panel-titulo">Reasignar cuota</h2>
              <button class="vx-btn vx-btn-sm vx-btn-texto" @click="reasignando = null">Cancelar</button>
            </div>
            <div class="p-3">
              <div class="vx-nota mb-3">
                <i class="bi bi-info-circle"></i>
                <span>
                  <strong>{{ reasignando.deuda.descripcion }}</strong> pasa a nombre de otra persona.
                  Sólo cambia esta cuota: las demás conservan su responsable.
                </span>
              </div>

              <label class="vx-etiqueta" for="reasignar-persona">Nuevo responsable</label>
              <div class="position-relative" style="max-width: 420px">
                <input
                  id="reasignar-persona"
                  v-model.trim="reasignando.busqueda"
                  class="vx-campo"
                  placeholder="Buscar persona…"
                  @input="buscarParaReasignar"
                >
                <ul
                  v-if="reasignando.resultados.length"
                  class="list-unstyled position-absolute w-100 mb-0 vx-panel"
                  data-test="resultados-reasignacion"
                  style="z-index: 20; box-shadow: var(--vx-sombra); max-height: 240px; overflow-y: auto"
                >
                  <li
                    v-for="c in reasignando.resultados"
                    :key="c.cliente_id"
                    class="px-3 py-2 border-bottom"
                    style="cursor: pointer"
                    @click="elegirDestino(c)"
                  >
                    <div style="font-size: 13px; font-weight: 550">{{ c.nombre_completo }}</div>
                    <div class="vx-celda-sub vx-num">{{ documentoDe(c) || 'Sin documento' }}</div>
                  </li>
                </ul>
              </div>

              <div v-if="reasignando.destino" class="vx-aviso mt-2">
                <div class="vx-aviso-titulo">
                  <i class="bi bi-person-check"></i> {{ reasignando.destino.nombre_completo }}
                </div>
              </div>

              <label class="vx-etiqueta mt-3" for="reasignar-motivo">Motivo</label>
              <input
                id="reasignar-motivo" v-model.trim="reasignando.motivo" class="vx-campo"
                placeholder="Por ejemplo: los padres se separaron y paga la madre"
                :class="{ 'vx-campo-error': errorReasignacion }"
              >
              <div v-if="errorReasignacion" class="vx-error" data-test="error-reasignacion">
                {{ errorReasignacion }}
              </div>

              <div class="d-flex justify-content-end gap-2 mt-3">
                <button class="vx-btn" @click="reasignando = null">Cancelar</button>
                <button class="vx-btn vx-btn-primario" data-test="btn-confirmar-reasignacion"
                        @click="confirmarReasignacion">
                  Reasignar
                </button>
              </div>
            </div>
          </div>

          <!-- Titulares -->
          <div class="vx-panel">
            <div class="vx-panel-cabecera">
              <h2 class="vx-panel-titulo">Titulares</h2>
              <button v-if="puedeAdministrar" class="vx-btn vx-btn-sm" @click="mostrarBuscador = !mostrarBuscador">
                <i class="bi bi-plus-lg"></i> Vincular
              </button>
            </div>

            <div v-if="mostrarBuscador" class="p-3 border-bottom">
              <div class="d-flex gap-2 align-items-start flex-wrap">
                <div class="position-relative" style="flex: 1 1 280px">
                  <input
                    v-model.trim="busqueda"
                    class="vx-campo vx-campo-sm"
                    placeholder="Buscar persona…"
                    @input="buscar"
                  >
                  <ul
                    v-if="resultados.length"
                    class="list-unstyled position-absolute w-100 mb-0 vx-panel"
              data-test="resultados-personas"
                    style="z-index: 20; box-shadow: var(--vx-sombra); max-height: 240px; overflow-y: auto"
                  >
                    <li
                      v-for="c in resultados"
                      :key="c.cliente_id"
                      class="px-3 py-2 border-bottom"
                      style="cursor: pointer"
                      @click="seleccionar(c)"
                    >
                      <div style="font-size: 13px; font-weight: 550">{{ c.nombre_completo }}</div>
                      <div class="vx-celda-sub vx-num">{{ documentoDe(c) || 'Sin documento' }}</div>
                    </li>
                  </ul>
                </div>
                <select v-model="rolNuevo" class="vx-campo vx-campo-sm" style="width: 190px" aria-label="Rol del nuevo titular">
                  <option v-for="r in ROLES_TITULAR" :key="r.valor" :value="r.valor">{{ r.texto }}</option>
                </select>
              </div>
              <div v-if="errorTitular" class="vx-ayuda text-danger mt-2">{{ errorTitular }}</div>
            </div>

            <div
              v-for="t in titulares"
              :key="t.cr_suscripcion_titular_id"
              class="d-flex align-items-center gap-3 px-3 py-2 border-bottom"
              :style="{ opacity: t.vigencia_hasta ? 0.55 : 1 }"
            >
              <div class="vx-iniciales">{{ iniciales(t.nombre_completo) }}</div>
              <div class="flex-grow-1 min-w-0">
                <!-- Cualquier titular puede tener deuda a su nombre —incluso
                     un cotitular, si una cuota se le reasignó (RN-6)—, así
                     que el link no se limita al responsable vigente. -->
                <router-link
                  v-if="puedeVerEstadoCuenta"
                  style="font-size: 13.5px; font-weight: 570; text-decoration: none"
                  :to="{ name: 'CobranzaEstadoCuenta', params: { clienteId: t.cliente_id } }"
                >
                  {{ t.nombre_completo }}
                </router-link>
                <div v-else style="font-size: 13.5px; font-weight: 570">{{ t.nombre_completo }}</div>
                <div class="vx-celda-sub vx-num">{{ documentoDe(t) || 'Sin documento' }}</div>
              </div>
              <div class="text-end">
                <span class="vx-estado" :class="t.rol === 'RESPONSABLE_PAGO' ? 'vx-estado-activa' : 'vx-estado-neutro'">
                  {{ rolTexto(t.rol) }}
                </span>
                <div class="vx-celda-sub vx-num mt-1">
                  {{ t.vigencia_hasta ? `Cerrado ${fechaCorta(t.vigencia_hasta)}` : `Desde ${fechaCorta(t.vigencia_desde)}` }}
                </div>
              </div>
              <!-- Al responsable de pago no se le cierra la vigencia y listo:
                   la suscripción quedaría sin nadie a quien cobrarle y dejaría
                   de generar deuda en silencio. Se RELEVA, que es una sola
                   operación: entra el nuevo y sale el anterior. -->
              <button
                v-if="puedeAdministrar && !t.vigencia_hasta && t.rol === 'RESPONSABLE_PAGO'"
                class="vx-btn vx-btn-sm"
                data-test="btn-cambiar-responsable"
                title="Cambiar el responsable de pago"
                @click="abrirRelevo"
              >
                Cambiar
              </button>
              <button
                v-else-if="puedeAdministrar && !t.vigencia_hasta"
                class="vx-btn vx-btn-sm"
                title="Cerrar vigencia"
                @click="cerrar(t)"
              >
                <i class="bi bi-x-lg"></i>
              </button>
            </div>

            <div v-if="relevo" class="p-3 border-top" data-test="panel-relevo">
              <div class="vx-nota mb-2">
                <i class="bi bi-info-circle"></i>
                <span>
                  El nuevo responsable entra y el anterior queda con su vigencia cerrada,
                  en una sola operación. Las cuotas <strong>ya generadas</strong> conservan
                  el responsable con el que nacieron.
                </span>
              </div>
              <label class="vx-etiqueta" for="relevo-persona">Nuevo responsable de pago</label>
              <div class="position-relative" style="max-width: 420px">
                <input
                  id="relevo-persona" v-model.trim="relevo.busqueda" class="vx-campo"
                  placeholder="Buscar persona…" @input="buscarParaRelevo"
                >
                <ul
                  v-if="relevo.resultados.length"
                  class="list-unstyled position-absolute w-100 mb-0 vx-panel"
                  data-test="resultados-relevo"
                  style="z-index: 20; box-shadow: var(--vx-sombra); max-height: 240px; overflow-y: auto"
                >
                  <li
                    v-for="c in relevo.resultados" :key="c.cliente_id"
                    class="px-3 py-2 border-bottom" style="cursor: pointer"
                    @click="confirmarRelevo(c)"
                  >
                    <div style="font-size: 13px; font-weight: 550">{{ c.nombre_completo }}</div>
                    <div class="vx-celda-sub vx-num">{{ documentoDe(c) || 'Sin documento' }}</div>
                  </li>
                </ul>
              </div>
              <div v-if="errorRelevo" class="vx-error mt-2" data-test="error-relevo">{{ errorRelevo }}</div>
              <div class="d-flex justify-content-end mt-2">
                <button class="vx-btn vx-btn-sm" @click="relevo = null">Cancelar</button>
              </div>
            </div>

            <div class="p-3 vx-nota">
              <i class="bi bi-info-circle"></i>
              <span>
                Los vínculos no se borran: se les cierra la vigencia. Cada cuota conserva
                el responsable con el que nació.
              </span>
            </div>
          </div>
        </div>

        <!-- Columna derecha -->
        <div class="col-lg-5">
          <div class="vx-panel mb-3">
            <div class="vx-panel-cabecera"><h2 class="vx-panel-titulo">Cobro</h2></div>
            <div class="px-3 pb-2">
              <div v-for="d in datosCobro" :key="d.etiqueta" class="d-flex justify-content-between gap-3 py-2 border-bottom">
                <span class="vx-ayuda">{{ d.etiqueta }}</span>
                <span style="font-size: 13px; font-weight: 550" class="text-end">{{ d.valor }}</span>
              </div>
            </div>
          </div>

          <!-- Descuentos y becas (E2, RN-13 a RN-16). Van en la suscripción y
               no en el esquema: la beca es de esta persona, no del molde. -->
          <div class="vx-panel mb-3">
            <div class="vx-panel-cabecera">
              <h2 class="vx-panel-titulo">Descuentos y becas</h2>
              <button
                v-if="puedeAdministrarDescuentos" class="vx-btn vx-btn-sm"
                @click="mostrarAltaDescuento = !mostrarAltaDescuento"
              >
                <i class="bi" :class="mostrarAltaDescuento ? 'bi-x-lg' : 'bi-plus-lg'"></i>
                {{ mostrarAltaDescuento ? 'Cancelar' : 'Agregar' }}
              </button>
            </div>

            <div v-if="mostrarAltaDescuento" class="p-3 border-bottom">
              <div class="row g-2">
                <div class="col-7">
                  <label class="vx-etiqueta" for="d-tipo">Tipo</label>
                  <select id="d-tipo" v-model="descuento.tipo" class="vx-campo vx-campo-sm">
                    <option v-for="t in TIPOS_DESCUENTO" :key="t.valor" :value="t.valor">
                      {{ t.texto }}
                    </option>
                  </select>
                </div>
                <div class="col-5">
                  <label class="vx-etiqueta" for="d-valor">
                    {{ descuento.tipo === 'PORCENTAJE' ? 'Porcentaje' : 'Monto' }}
                  </label>
                  <input
                    id="d-valor" v-model.number="descuento.valor" class="vx-campo vx-campo-sm vx-num"
                    type="number" min="0"
                    :class="{ 'vx-campo-error': errorDescuento('valor') }"
                  >
                </div>
                <div v-if="errorDescuento('valor')" class="col-12">
                  <div class="vx-error">{{ errorDescuento('valor') }}</div>
                </div>
                <div class="col-12">
                  <label class="vx-etiqueta" for="d-motivo">Motivo</label>
                  <input
                    id="d-motivo" v-model.trim="descuento.motivo" class="vx-campo vx-campo-sm"
                    placeholder="Beca por hermanos"
                    :class="{ 'vx-campo-error': errorDescuento('motivo') }"
                  >
                  <div v-if="errorDescuento('motivo')" class="vx-error">{{ errorDescuento('motivo') }}</div>
                  <div v-else class="vx-ayuda mt-1">
                    Es lo que explica, dentro de seis meses, por qué esta persona paga menos.
                  </div>
                </div>
                <div class="col-6">
                  <label class="vx-etiqueta" for="d-desde">Vigente desde</label>
                  <input id="d-desde" v-model="descuento.vigencia_desde" class="vx-campo vx-campo-sm" type="date">
                </div>
                <div class="col-6">
                  <label class="vx-etiqueta" for="d-hasta">Hasta</label>
                  <input
                    id="d-hasta" v-model="descuento.vigencia_hasta" class="vx-campo vx-campo-sm" type="date"
                    :class="{ 'vx-campo-error': errorDescuento('vigencia_hasta') }"
                  >
                  <div v-if="errorDescuento('vigencia_hasta')" class="vx-error">
                    {{ errorDescuento('vigencia_hasta') }}
                  </div>
                </div>
              </div>

              <div v-if="errorAltaDescuento" class="vx-error mt-2">{{ errorAltaDescuento }}</div>

              <div class="d-flex justify-content-end mt-2">
                <button class="vx-btn vx-btn-sm" :disabled="guardandoDescuento" @click="agregarDescuento">
                  <span v-if="guardandoDescuento" class="spinner-border spinner-border-sm"></span>
                  Agregar descuento
                </button>
              </div>
            </div>

            <!-- RN-79: el aviso de incoherencia. Era el defecto de fondo — dos
                 paneles contando cosas distintas y nada que dijera cuál mentía. -->
            <div
              v-if="estadoDesc.sin_aplicar && !propuesta" class="p-3 vx-nota"
              style="border-left: 3px solid #b45309" data-test="aviso-sin-aplicar"
            >
              <i class="bi bi-exclamation-triangle"></i>
              <span>
                Hay un descuento cargado que <strong>no está aplicado</strong> en
                {{ cuotasSinAplicar }}
                {{ cuotasSinAplicar === 1 ? 'cuota' : 'cuotas' }}.
                <button class="vx-btn vx-btn-sm ms-2" data-test="btn-aplicar-pendiente"
                        @click="previsualizarSoloVigentes">
                  Ver y aplicar
                </button>
              </span>
            </div>

            <!-- Paso 2 (RN-73): qué cambia, antes de que cambie. -->
            <div v-if="propuesta" class="p-3 border-bottom" data-test="propuesta-recalculo">
              <div class="d-flex justify-content-between align-items-center mb-2">
                <strong style="font-size: 13.5px">{{ tituloPropuesta }}</strong>
                <span
                  class="vx-num" data-test="diferencia-total"
                  :style="{ fontWeight: 600, color: diferencia > 0 ? '#b91c1c' : '#15803d' }"
                >{{ diferenciaTexto }}</span>
              </div>

              <div v-if="!propuesta.al_dia.length && !propuesta.vencidas.length" class="vx-ayuda">
                Ninguna cuota cambia. Las que hay están cobradas, anuladas o ya tienen
                este descuento aplicado.
              </div>

              <div v-for="c in propuesta.al_dia" :key="c.cr_deuda_id"
                   class="d-flex justify-content-between gap-2 py-1">
                <span class="vx-celda-sub">{{ c.periodo }}</span>
                <span class="vx-num" style="font-size: 12.5px">
                  {{ formatearMonto(c.monto_total_actual) }} →
                  <strong>{{ formatearMonto(c.monto_total_nuevo) }}</strong>
                </span>
              </div>

              <!-- RN-70.1: las vencidas van aparte y apagadas por defecto.
                   Bajarle el importe a quien todavía no pagó es benigno; lo que
                   no puede pasar es que se mueva sin que nadie lo decida. -->
              <div v-if="propuesta.vencidas.length" class="mt-2 pt-2 border-top">
                <label class="d-flex align-items-start gap-2" style="cursor: pointer">
                  <input v-model="incluirVencidas" type="checkbox" class="mt-1"
                         data-test="incluir-vencidas" @change="reprevisualizar">
                  <span class="vx-ayuda">
                    Incluir {{ propuesta.vencidas.length }}
                    {{ propuesta.vencidas.length === 1 ? 'cuota vencida' : 'cuotas vencidas' }}
                    e impaga{{ propuesta.vencidas.length === 1 ? '' : 's' }}.
                    Ya se les comunicó un importe a la familia.
                  </span>
                </label>
                <div v-for="c in propuesta.vencidas" :key="c.cr_deuda_id"
                     class="d-flex justify-content-between gap-2 py-1 ps-4">
                  <span class="vx-celda-sub">{{ c.periodo }}</span>
                  <span class="vx-num" style="font-size: 12.5px">
                    {{ formatearMonto(c.monto_total_actual) }} →
                    {{ formatearMonto(c.monto_total_nuevo) }}
                  </span>
                </div>
              </div>

              <div v-if="intactasVisibles.length" class="mt-2 pt-2 border-top">
                <div class="vx-ayuda mb-1">No se tocan:</div>
                <div v-for="i in intactasVisibles" :key="i.cr_deuda_id" class="vx-celda-sub">
                  {{ i.periodo }} — {{ i.motivo }}
                </div>
              </div>

              <div v-if="errorPropuesta" class="vx-error mt-2">{{ errorPropuesta }}</div>

              <div class="d-flex justify-content-end gap-2 mt-3">
                <button class="vx-btn vx-btn-sm" data-test="btn-cancelar-recalculo"
                        @click="cancelarPropuesta">Cancelar</button>
                <button
                  class="vx-btn vx-btn-sm vx-btn-primario" :disabled="aplicando"
                  data-test="btn-confirmar-recalculo" @click="confirmarPropuesta"
                >
                  <span v-if="aplicando" class="spinner-border spinner-border-sm"></span>
                  Confirmar
                </button>
              </div>
            </div>

            <div v-if="!descuentos.length" class="vx-vacio" style="padding: 24px 18px">
              <span class="vx-vacio-texto">
                Sin descuentos. Las cuotas se generan por el monto completo del concepto.
              </span>
            </div>

            <div
              v-for="d in descuentos" :key="d.cr_descuento_id"
              class="d-flex align-items-center gap-2 px-3 py-2 border-bottom"
            >
              <div class="flex-grow-1 min-w-0">
                <div style="font-size: 13.5px; font-weight: 570">{{ d.motivo }}</div>
                <div class="vx-celda-sub vx-num">{{ vigenciaTexto(d) }}</div>
              </div>
              <span class="vx-estado vx-estado-activa vx-num">{{ descuentoTexto(d) }}</span>
              <button
                v-if="puedeAdministrarDescuentos" class="vx-btn vx-btn-sm" title="Quitar"
                @click="quitarDescuento(d)"
              >
                <i class="bi bi-trash"></i>
              </button>
            </div>

            <div class="p-3 vx-nota">
              <i class="bi bi-info-circle"></i>
              <span>
                Agregar o quitar un descuento <strong>recalcula las cuotas que todavía
                no se cobraron</strong>, y siempre muestra qué cambia antes de hacerlo.
                Las cobradas y las facturadas no se tocan.
              </span>
            </div>
          </div>

          <div v-if="suscripcion.ficha" class="vx-panel">
            <div class="vx-panel-cabecera"><h2 class="vx-panel-titulo">Ficha</h2></div>
            <div class="px-3 pb-2">
              <div v-for="d in datosFicha" :key="d.etiqueta" class="d-flex justify-content-between gap-3 py-2 border-bottom">
                <span class="vx-ayuda">{{ d.etiqueta }}</span>
                <span style="font-size: 13px; font-weight: 550" class="text-end">{{ d.valor }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
// Detalle de suscripción — CR-E1-006, extendido en CR-E2-006 con las cuotas
// generadas y los descuentos.
// SPEC RN-2, RN-6, RN-10, RN-13 a RN-16, RN-19 · diseño aprobado 2026-08-31.

import { ref, reactive, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import AppNavbar from '@/components/AppNavbar.vue'
import { useNotificationStore } from '@/stores/notifications'
import { hasAnyPermission } from '@/services/authSessionService'
import {
  obtenerSuscripcion, actualizarSuscripcion, agregarTitular, cerrarTitular,
  buscarClientes, listarDeudas, listarDescuentos,
  previsualizarDescuentos, confirmarDescuentos, estadoDescuentos,
  previsualizarBaja, darDeBaja, reasignarDeuda, cambiarResponsable, MOTIVO_INTOCABLE,
  obtenerVocabulario, VOCABULARIO_POR_DEFECTO, TIPOS_DESCUENTO, descuentoTexto,
  estadoDeuda, formatearMonto,
  ROLES_TITULAR, rolTexto, iniciales, documentoDe, fechaCorta
} from '@/services/cobranzaService'
import { validarDescuento } from '@/services/cobranzaSchemas'
import { fechaIsoLocal } from '@/utils/fechaLocal.mjs'

const route = useRoute()
const notificaciones = useNotificationStore()

const suscripcion = ref(null)
const cargando = ref(true)
const guardando = ref(false)
const error = ref('')
const errorTitular = ref('')
const mostrarBuscador = ref(false)
const busqueda = ref('')
const resultados = ref([])
const rolNuevo = ref('COTITULAR')

const deudas = ref([])
const cargandoDeudas = ref(true)
const descuentos = ref([])
const mostrarAltaDescuento = ref(false)
const guardandoDescuento = ref(false)
const errorAltaDescuento = ref('')
const tocadoDescuento = ref(false)

const puedeAdministrar = computed(() => hasAnyPermission(['administrar_suscripciones']))
const puedeAdministrarDescuentos = computed(() => hasAnyPermission(['administrar_descuentos']))
const puedeGenerar = computed(() => hasAnyPermission(['generar_deudas']))
const puedeReasignar = computed(() => hasAnyPermission(['reasignar_deuda']))
const puedeVerEstadoCuenta = computed(() => hasAnyPermission(['ver_estado_cuenta']))

// --- Baja de la suscripción (RN-23, D6) ---
const baja = ref(null)
const cargandoBaja = ref(false)
const guardandoBaja = ref(false)
const errorBaja = ref('')

const resumenIntocables = computed(() => {
  if (!baja.value?.intocables?.length) return ''
  const porMotivo = {}
  for (const d of baja.value.intocables) {
    porMotivo[d.motivo] = (porMotivo[d.motivo] || 0) + 1
  }
  return Object.entries(porMotivo)
    .map(([motivo, n]) => `${n} ${(MOTIVO_INTOCABLE[motivo] || motivo).toLowerCase()}`)
    .join(', ')
})

// --- Relevo del responsable de pago (RN-3, RN-6) ---
const relevo = ref(null)
const errorRelevo = ref('')

// --- Reasignación de una cuota (RN-6) ---
const reasignando = ref(null)
const errorReasignacion = ref('')

// Una cuota con pagos aplicados NUNCA se reasigna. Acá se usa el saldo como
// indicio —si bajó, hubo un pago— y el backend es el que decide de verdad,
// porque también mira las aplicaciones revertidas.
const esReasignable = (d) =>
  d.estado !== 'ANULADA' && Number(d.saldo) === Number(d.monto_total)

const etiquetas = ref({ ...VOCABULARIO_POR_DEFECTO })
const vocabulario = computed(() => ({
  singular: etiquetas.value['deuda.singular'],
  plural: etiquetas.value['deuda.plural']
}))

const descuento = reactive({
  tipo: 'PORCENTAJE', valor: null, motivo: '', vigencia_desde: '', vigencia_hasta: ''
})

// El tope de la suma de porcentajes (RN-15) se anticipa acá con los descuentos
// que la suscripción ya tiene. La garantía sigue siendo el backend: esto sólo
// evita que el operador complete el formulario para enterarse ahí.
const erroresDescuento = computed(() =>
  validarDescuento(descuento, { vigentes: descuentos.value })
)
const errorDescuento = (campo) =>
  (tocadoDescuento.value ? erroresDescuento.value[campo] : '') || ''

const saldoTotal = computed(() =>
  deudas.value.reduce((acc, d) => acc + Number(d.saldo), 0)
)

const vigenciaTexto = (d) => {
  const desde = d.vigencia_desde ? `Desde ${fechaCorta(d.vigencia_desde)}` : 'Sin fecha de inicio'
  return d.vigencia_hasta ? `${desde} · hasta ${fechaCorta(d.vigencia_hasta)}` : desde
}

// Los cerrados van al final: lo vigente es lo que el operador necesita ver
// primero, pero la historia no se esconde (RN-6).
const titulares = computed(() => {
  const lista = suscripcion.value?.titulares || []
  return [...lista].sort((a, b) => Number(Boolean(a.vigencia_hasta)) - Number(Boolean(b.vigencia_hasta)))
})

const titulo = computed(() =>
  suscripcion.value?.ficha?.cabecera?.nombre || `Suscripción #${suscripcion.value?.cr_suscripcion_id}`
)

const subtitulo = computed(() => {
  const cab = suscripcion.value?.ficha?.cabecera
  if (!cab) return 'Sin ficha cargada'
  const detalle = Object.values(suscripcion.value?.ficha?.detalle || {}).filter(Boolean).join(' · ')
  return [cab.descripcion, cab.id, detalle].filter(Boolean).join(' · ')
})

const textoEstado = computed(() =>
  ({ ACTIVA: 'Activa', SUSPENDIDA: 'Suspendida', FINALIZADA: 'Finalizada' })[suscripcion.value?.estado] || ''
)

const claseEstado = computed(() =>
  suscripcion.value?.estado === 'ACTIVA' ? 'vx-estado-activa' : 'vx-estado-neutro'
)

const datosCobro = computed(() => [
  { etiqueta: 'Esquema', valor: suscripcion.value?.esquema_nombre || '—' },
  { etiqueta: 'Documento', valor: suscripcion.value?.documento_preferido === 'RECIBO' ? 'Recibo de dinero' : 'Factura electrónica' },
  { etiqueta: 'Vigencia desde', valor: fechaCorta(suscripcion.value?.vigencia_desde) },
  { etiqueta: 'Vigencia hasta', valor: suscripcion.value?.vigencia_hasta ? fechaCorta(suscripcion.value.vigencia_hasta) : 'Sin fin' }
])

const datosFicha = computed(() => {
  const f = suscripcion.value?.ficha
  if (!f) return []
  return [
    { etiqueta: 'Nombre', valor: f.cabecera.nombre },
    { etiqueta: 'Qué es', valor: f.cabecera.descripcion },
    { etiqueta: 'Identificador', valor: f.cabecera.id },
    ...Object.entries(f.detalle || {}).map(([k, v]) => ({ etiqueta: k, valor: v }))
  ]
})

async function cargar () {
  cargando.value = true
  error.value = ''
  try {
    suscripcion.value = await obtenerSuscripcion(route.params.id)
  } catch (err) {
    error.value = err?.userMessage || 'Error inesperado.'
  } finally {
    cargando.value = false
  }
}

// Cuotas y descuentos se cargan aparte de la suscripción: son dos listas que
// pueden fallar por su cuenta, y que una de ellas no responda no tiene por qué
// dejar la pantalla entera en blanco.
async function cargarCuotas () {
  cargandoDeudas.value = true
  try {
    deudas.value = await listarDeudas({ suscripcion_id: route.params.id })
  } catch {
    deudas.value = []
  } finally {
    cargandoDeudas.value = false
  }
}

async function cargarDescuentos () {
  try {
    descuentos.value = await listarDescuentos(route.params.id)
  } catch {
    descuentos.value = []
  }
  await cargarEstadoDescuentos()
}

// RN-79. No es una consulta más: es la que hace que la pantalla deje de mentir.
// Si falla, se calla —un aviso que no se puede calcular no se inventa—.
async function cargarEstadoDescuentos () {
  try {
    estadoDesc.value = await estadoDescuentos(route.params.id)
  } catch {
    estadoDesc.value = { sin_aplicar: false, al_dia: [], vencidas: [] }
  }
}

// --- El recálculo, en dos pasos (RN-73) ---------------------------------
//
// `operacion` guarda lo que el operador pidió, para que confirmar mande
// exactamente lo mismo que se previsualizó. El servidor igual recalcula (RN-76):
// esto es para que la pantalla no se contradiga sola.
const propuesta = ref(null)
const operacion = ref(null)
const incluirVencidas = ref(false)
const aplicando = ref(false)
const errorPropuesta = ref('')
const estadoDesc = ref({ sin_aplicar: false, al_dia: [], vencidas: [] })

const cuotasSinAplicar = computed(
  () => (estadoDesc.value.al_dia?.length || 0) + (estadoDesc.value.vencidas?.length || 0)
)

const diferencia = computed(() => Number(propuesta.value?.resumen?.diferencia_total || 0))

// Con signo y en palabras. Quitar una beca SUBE importes, y eso tiene que
// leerse distinto de bajarlos: es el camino que más daño hace.
const diferenciaTexto = computed(() => {
  const d = diferencia.value
  if (!d) return 'sin cambio'
  const monto = formatearMonto(Math.abs(d), suscripcion.value?.moneda || 'PYG')
  return d < 0 ? `${monto} menos` : `${monto} MÁS`
})

const tituloPropuesta = computed(() => {
  const n = propuesta.value?.resumen?.cantidad || 0
  if (!n) return 'Ninguna cuota cambia'
  return `${n} ${n === 1 ? 'cuota cambia' : 'cuotas cambian'}`
})

// Las intactas se muestran, pero no las cobradas de hace meses: el operador
// necesita entender por qué una cuota no se movió, no leer el año entero.
const intactasVisibles = computed(() => (propuesta.value?.intactas || []).slice(0, 6))

async function pedirPropuesta (op) {
  errorPropuesta.value = ''
  try {
    operacion.value = op
    propuesta.value = await previsualizarDescuentos(route.params.id, {
      ...op, incluir_vencidas: incluirVencidas.value
    })
  } catch (err) {
    propuesta.value = null
    operacion.value = null
    notificaciones.notify({
      type: 'error', message: err?.userMessage || 'No se pudo calcular el cambio.'
    })
  }
}

// El aviso de RN-79 con el conjunto que YA está cargado: no agrega ni quita
// nada, sólo aplica lo que debería estar aplicado.
const reprevisualizar = () => (operacion.value ? pedirPropuesta(operacion.value) : null)

function cancelarPropuesta () {
  propuesta.value = null
  operacion.value = null
  incluirVencidas.value = false
  errorPropuesta.value = ''
}

async function confirmarPropuesta () {
  aplicando.value = true
  errorPropuesta.value = ''
  try {
    const r = await confirmarDescuentos(route.params.id, {
      ...operacion.value, incluir_vencidas: incluirVencidas.value
    })
    cancelarPropuesta()
    mostrarAltaDescuento.value = false
    await Promise.all([cargarCuotas(), cargarDescuentos()])
    notificaciones.notify({
      type: 'success',
      message: r.aplicadas
        ? `Listo: ${r.aplicadas} ${r.aplicadas === 1 ? 'cuota actualizada' : 'cuotas actualizadas'}.`
        : 'Guardado. Ninguna cuota necesitaba cambio.'
    })
  } catch (err) {
    errorPropuesta.value = err?.userMessage || 'No se pudo aplicar el cambio.'
  } finally {
    aplicando.value = false
  }
}

async function agregarDescuento () {
  tocadoDescuento.value = true
  errorAltaDescuento.value = ''
  if (Object.keys(erroresDescuento.value).length) return

  guardandoDescuento.value = true
  incluirVencidas.value = false
  try {
    // Ya NO crea: propone. El descuento se escribe recién al confirmar, junto
    // con el recálculo, en una sola transacción.
    await pedirPropuesta({
      agregar: {
        tipo: descuento.tipo,
        valor: descuento.valor,
        motivo: descuento.motivo,
        vigencia_desde: descuento.vigencia_desde || null,
        vigencia_hasta: descuento.vigencia_hasta || null
      },
      motivo: descuento.motivo
    })
    if (propuesta.value) {
      Object.assign(descuento, {
        tipo: 'PORCENTAJE', valor: null, motivo: '', vigencia_desde: '', vigencia_hasta: ''
      })
      tocadoDescuento.value = false
    }
  } finally {
    guardandoDescuento.value = false
  }
}

// Aplicar lo que ya está cargado y no llegó a las cuotas (RN-79). No agrega ni
// quita: el conjunto vigente es el que es, y lo único que falta es que las
// cuotas lo reflejen.
async function previsualizarSoloVigentes () {
  incluirVencidas.value = false
  // `aplicar_vigentes` es la tercera forma del contrato: no agrega ni quita
  // nada, sólo hace que las cuotas reflejen lo que ya está cargado.
  await pedirPropuesta({ aplicar_vigentes: true, motivo: 'Aplicar descuento ya cargado' })
}

async function quitarDescuento (d) {
  // Artículo 6: quitar un descuento SUBE importes. Sin motivo no se hace, y sin
  // que el operador vea cuánto sube, tampoco.
  const motivo = window.prompt(`¿Por qué se quita «${d.motivo}»?`)
  if (!motivo || !motivo.trim()) return
  incluirVencidas.value = false
  await pedirPropuesta({ quitar: [d.cr_descuento_id], motivo: motivo.trim() })
}

let temporizador = null
function buscar () {
  clearTimeout(temporizador)
  if (busqueda.value.length < 2) { resultados.value = []; return }
  temporizador = setTimeout(async () => {
    try {
      const encontrados = await buscarClientes(busqueda.value)
      const vigentes = new Set(
        titulares.value.filter((t) => !t.vigencia_hasta).map((t) => t.cliente_id)
      )
      resultados.value = encontrados.filter((c) => !vigentes.has(c.cliente_id))
    } catch {
      resultados.value = []
    }
  }, 250)
}

async function seleccionar (cliente) {
  errorTitular.value = ''
  try {
    await agregarTitular(suscripcion.value.cr_suscripcion_id, {
      cliente_id: cliente.cliente_id,
      rol: rolNuevo.value
    })
    busqueda.value = ''
    resultados.value = []
    mostrarBuscador.value = false
    await cargar()
    notificaciones.notify({ type: 'success', message: 'Persona vinculada.' })
  } catch (err) {
    // El rechazo del segundo responsable de pago llega acá con su mensaje
    // propio: es una regla de negocio, no un fallo (RN-3, D3).
    errorTitular.value = err?.userMessage || 'No se pudo vincular.'
  }
}

async function cerrar (titular) {
  try {
    await cerrarTitular(titular.cr_suscripcion_titular_id)
    await cargar()
    notificaciones.notify({ type: 'success', message: 'Vigencia cerrada.' })
  } catch (err) {
    notificaciones.notify({ type: 'error', message: err?.userMessage || 'No se pudo cerrar el vínculo.' })
  }
}

async function alternarSuspension () {
  guardando.value = true
  try {
    const estado = suscripcion.value.estado === 'SUSPENDIDA' ? 'ACTIVA' : 'SUSPENDIDA'
    await actualizarSuscripcion(suscripcion.value.cr_suscripcion_id, { estado })
    await cargar()
  } catch (err) {
    notificaciones.notify({ type: 'error', message: err?.userMessage || 'No se pudo cambiar el estado.' })
  } finally {
    guardando.value = false
  }
}

function abrirBaja () {
  errorBaja.value = ''
  baja.value = {
    // Por defecto, hoy: dar de baja es un hecho del presente. La fecha se
    // puede mover si la baja fue acordada antes.
    vigencia_hasta: fechaIsoLocal(),
    motivo: '',
    anular_futuras: true,
    anulables: [],
    intocables: [],
    monto_anulable: 0,
    previsualizacion: false
  }
  recalcularBaja()
}

// La previsualización la calcula el BACKEND, no la pantalla: es la misma
// consulta que después decide qué anular, así que lo que se ve es lo que va a
// pasar — no una aproximación.
async function recalcularBaja () {
  if (!baja.value) return
  cargandoBaja.value = true
  errorBaja.value = ''
  try {
    const previo = await previsualizarBaja(suscripcion.value.cr_suscripcion_id, {
      vigencia_hasta: baja.value.vigencia_hasta
    })
    Object.assign(baja.value, {
      anulables: previo.anulables || [],
      intocables: previo.intocables || [],
      monto_anulable: previo.monto_anulable || 0,
      previsualizacion: true
    })
  } catch (err) {
    errorBaja.value = err?.userMessage || 'No se pudo calcular la baja.'
  } finally {
    cargandoBaja.value = false
  }
}

async function confirmarBaja () {
  if (!baja.value.motivo) {
    errorBaja.value = 'Indicá el motivo de la baja.'
    return
  }
  guardandoBaja.value = true
  errorBaja.value = ''
  try {
    const r = await darDeBaja(suscripcion.value.cr_suscripcion_id, {
      vigencia_hasta: baja.value.vigencia_hasta,
      motivo: baja.value.motivo,
      anular_futuras: baja.value.anular_futuras
    })
    baja.value = null
    notificaciones.notify({
      type: 'success',
      message: r.anuladas.length
        ? `Suscripción dada de baja. ${r.anuladas.length} cuota(s) anuladas.`
        : 'Suscripción dada de baja.'
    })
    await cargar()
    await cargarCuotas()
  } catch (err) {
    errorBaja.value = err?.userMessage || 'No se pudo dar de baja la suscripción.'
  } finally {
    guardandoBaja.value = false
  }
}

function abrirRelevo () {
  errorRelevo.value = ''
  relevo.value = { busqueda: '', resultados: [] }
}

let temporizadorRelevo = null
function buscarParaRelevo () {
  clearTimeout(temporizadorRelevo)
  if (relevo.value.busqueda.length < 2) { relevo.value.resultados = []; return }
  temporizadorRelevo = setTimeout(async () => {
    try {
      const encontrados = await buscarClientes(relevo.value.busqueda)
      const yaVinculados = new Set(
        titulares.value.filter((t) => !t.vigencia_hasta).map((t) => t.cliente_id)
      )
      relevo.value.resultados = encontrados.filter((c) => !yaVinculados.has(c.cliente_id))
    } catch {
      relevo.value.resultados = []
    }
  }, 250)
}

async function confirmarRelevo (cliente) {
  errorRelevo.value = ''
  try {
    await cambiarResponsable(suscripcion.value.cr_suscripcion_id, { cliente_id: cliente.cliente_id })
    relevo.value = null
    notificaciones.notify({ type: 'success', message: 'Responsable de pago cambiado.' })
    await cargar()
  } catch (err) {
    errorRelevo.value = err?.userMessage || 'No se pudo cambiar el responsable.'
  }
}

function abrirReasignacion (deuda) {
  errorReasignacion.value = ''
  reasignando.value = { deuda, busqueda: '', resultados: [], destino: null, motivo: '' }
}

let temporizadorReasignar = null
function buscarParaReasignar () {
  clearTimeout(temporizadorReasignar)
  reasignando.value.destino = null
  if (reasignando.value.busqueda.length < 2) { reasignando.value.resultados = []; return }
  temporizadorReasignar = setTimeout(async () => {
    try {
      reasignando.value.resultados = await buscarClientes(reasignando.value.busqueda)
    } catch {
      reasignando.value.resultados = []
    }
  }, 250)
}

function elegirDestino (cliente) {
  reasignando.value.destino = cliente
  reasignando.value.resultados = []
  reasignando.value.busqueda = cliente.nombre_completo
}

async function confirmarReasignacion () {
  if (!reasignando.value.destino) {
    errorReasignacion.value = 'Elegí a quién se le reasigna la cuota.'
    return
  }
  if (!reasignando.value.motivo) {
    errorReasignacion.value = 'Indicá el motivo de la reasignación.'
    return
  }
  try {
    await reasignarDeuda(reasignando.value.deuda.cr_deuda_id, {
      cliente_id: reasignando.value.destino.cliente_id,
      motivo: reasignando.value.motivo
    })
    reasignando.value = null
    notificaciones.notify({ type: 'success', message: 'Cuota reasignada.' })
    await cargarCuotas()
  } catch (err) {
    errorReasignacion.value = err?.userMessage || 'No se pudo reasignar la cuota.'
  }
}

onMounted(async () => {
  etiquetas.value = await obtenerVocabulario()
  await cargar()
  await Promise.all([cargarCuotas(), cargarDescuentos()])
})
</script>
